/**
 * Pure "should we show the conversation rating card, given who's asking"
 * decision for ConversationRatingCard's four triggers (idle / end_chat /
 * handoff_wrap / menu — see page.tsx's wiring). Factored out as a plain
 * function — no React, no effects — per this task's brief: "Put the should-
 * we-ask / which-source decision in a pure, unit-tested function; do not
 * scatter it across effects." Every effect that could fire the card
 * (page.tsx's idle timer, its handoff-ended-normally watcher, the header
 * menu's onGiveFeedback, the close-button gate) just gathers its own signals
 * and calls this — none of them re-implement the gating rules themselves.
 */
import {
  FeedbackSource,
  type FeedbackSource as FeedbackSourceValue,
} from '@/lib/api/feedback';
import { HandoffPhase } from '@/types/chat';

// Re-exported so call sites (page.tsx, conversation-rating-card.tsx) can get
// the source vocabulary from this one module alongside the ask function,
// without a second import from lib/api/feedback just for the enum.
export { FeedbackSource };

/**
 * Idle-inactivity threshold before the IDLE trigger is allowed to fire.
 * Named + isolated on its own so it's a one-line change, per the brief's
 * "keep the timing constant named and easy to change."
 */
export const FEEDBACK_IDLE_TIMEOUT_MS = 3 * 60 * 1000;

/**
 * The sources this gate actually arbitrates: the four "we opened an ask"
 * moments (IDLE / END_CHAT / HANDOFF_WRAP / MENU). FeedbackSource also has a
 * fifth value, MESSAGE_ROW — an unprompted per-message thumb — but it's
 * deliberately excluded from this union rather than left accepted-but-
 * unhandled. A message-row thumb never goes through "should we ask" at all
 * (message-reaction-row.tsx calls submitFeedback directly, no gate step), so
 * letting it type-check here would either silently subject a raw reaction to
 * a cap meant for asks, or — worse — let a future caller's thumb submission
 * silently consume this conversation's one-ask budget. Excluding it makes
 * that a compile error instead of a latent behavioral bug.
 */
export type FeedbackAskSource = Exclude<
  FeedbackSourceValue,
  typeof FeedbackSource.MESSAGE_ROW
>;

export interface FeedbackAskContext {
  source: FeedbackAskSource;
  /** Has the rating card already been shown this conversation, from ANY
   *  trigger? Ignored for MENU — opening it from the header menu is an
   *  explicit, repeatable user request, exempt from the once-per-
   *  conversation cap (and does NOT itself set this flag going forward —
   *  see page.tsx: a manual menu ask never blocks a later idle/end_chat/
   *  handoff_wrap ask). */
  alreadyAskedThisConversation: boolean;
  /** A live handoff is in progress. Only checked for IDLE — the design
   *  explicitly calls out "never while a handoff is live" for the idle
   *  trigger; the other three sources each fire at a moment a handoff
   *  can't plausibly be live anyway (chat ended / handoff itself just
   *  ended / user opened a menu). */
  handoffLive: boolean;
  /** The composer currently holds unsent draft text. Only checked for
   *  IDLE, same rationale as handoffLive — interrupting someone mid-typing
   *  is exactly the "blocks the conversation" failure mode this guards. */
  hasDraftText: boolean;
}

/**
 * Whether a handoff is genuinely in progress right now — the IDLE trigger's
 * "never ask while a handoff is live" guard (`FeedbackAskContext.handoffLive`
 * above) needs an ALLOW-list of the phases where a rep is still expected
 * (COLLECTING — the pre-handoff contact form — and WAITING) or present
 * (CONNECTED), not a "phase !== ENDED" deny-list. HandoffPhase.TIMED_OUT is
 * a terminal phase exactly like ENDED — no rep is coming — but nothing ever
 * transitions a handoff OUT of TIMED_OUT within a session (see that phase's
 * JSDoc in types/chat.ts), so a deny-list keyed only on ENDED reads a
 * timed-out handoff as "live" forever, permanently suppressing the idle
 * rating ask for the rest of the conversation — exactly the session most
 * worth measuring, since trigger 1 (handoff_wrap) is separately skipped for
 * TIMEOUT_NO_AGENT. page.tsx computes `handoffLive` from this rather than
 * re-deriving its own phase check.
 */
export function isHandoffLive(phase: HandoffPhase | null | undefined): boolean {
  return (
    phase === HandoffPhase.COLLECTING ||
    phase === HandoffPhase.WAITING ||
    phase === HandoffPhase.CONNECTED
  );
}

/** Returns whether ConversationRatingCard should be shown for this ask. */
export function shouldAskForFeedback(ctx: FeedbackAskContext): boolean {
  // An explicit "Give feedback" menu click always shows the card — it's a
  // deliberate request, not an interruption, so none of the ambient guards
  // (already-asked, handoff-live, draft-text) apply to it.
  if (ctx.source === FeedbackSource.MENU) return true;

  if (ctx.alreadyAskedThisConversation) return false;

  if (ctx.source === FeedbackSource.IDLE) {
    return !ctx.handoffLive && !ctx.hasDraftText;
  }

  // END_CHAT / HANDOFF_WRAP: the once-per-conversation gate above is the
  // only rule — both already fire at a moment-in-time the caller controls
  // (the close action, the handoff's own end transition), so there's
  // nothing further to check here.
  return true;
}
